import { useCallback, useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import Information, { INFORMATION_SIZE } from 'src/components/Information';
import { BENCHMARK_SOURCE, getEmailChannelsFromAssetType } from 'src/data/benchmark';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { getIsEmailChannel } from 'src/data/performance';

import { Connect } from './components/Connect';
import { ConnectedBenchmark } from './components/ConnectedBenchmark';
import { EmptyResults } from './components/EmptyResults';
import { Loader } from './components/Loader';
import { Upgrade } from './components/Upgrade';
import { useStyles } from './useStyles';

const MIN_CHANNEL_ACCOUNTS = 2;

export const Benchmark = ({
  classes,
  onBenchmarkConnectClick,
  channel,
  benchmarkConfig,
  connectedAccounts,
  loadingStatusMap,
  loadingBenchmark,
  source,
  shouldUpgrade,
  featureLimitsData,
  openPopup,
  onBenchmarkUpgradeClick,
  openUpgradePopup,
  assetType,
  generationTool,
}) => {
  const styles = useStyles({ classes });

  const { limit, currentCount, limitedAccountData } = featureLimitsData;
  const isFeatureLimited = limit && (!!limitedAccountData?.accountId || limit <= currentCount);

  const isEmailChannel = getIsEmailChannel(channel);

  const emailChannels = useMemo(() => getEmailChannelsFromAssetType(assetType), [assetType]);

  const alignedConnectedAccounts = useMemo(() => {
    if (isEmailChannel) {
      return emailChannels.flatMap((key) => connectedAccounts?.[key] || []);
    }
    return connectedAccounts?.[channel] || [];
  }, [isEmailChannel, connectedAccounts, emailChannels, channel]);

  const isChannelConnected = alignedConnectedAccounts?.length > 0;

  const handleConnectClick = useCallback(() => {
    if (!isChannelConnected && isFeatureLimited) {
      openUpgradePopup(FEATURE_LIMITS.resourcesRowsPerCustomer);
      onBenchmarkUpgradeClick();
      return;
    }
    onBenchmarkConnectClick?.(channel);
  }, [
    onBenchmarkUpgradeClick,
    channel,
    onBenchmarkConnectClick,
    isChannelConnected,
    openUpgradePopup,
    isFeatureLimited,
  ]);

  const isAnyEmailChannelLoading = emailChannels.some((channel) =>
    Object.values(loadingStatusMap?.[channel] || {}).some((status) => status === true)
  );

  const isChannelLoading = Object.values(loadingStatusMap?.[channel] || {}).some(
    (status) => status === true
  );

  const isLoadingChannelCopies = isEmailChannel
    ? isAnyEmailChannelLoading && alignedConnectedAccounts.length < MIN_CHANNEL_ACCOUNTS
    : isChannelLoading;

  const hasCustomScore = benchmarkConfig?.customScore > 0;

  const connectedAndHasBenchmark =
    isChannelConnected &&
    hasCustomScore &&
    !benchmarkConfig?.shouldUpgrade &&
    !isLoadingChannelCopies;

  const shouldShowUpgradeSection = shouldUpgrade && connectedAndHasBenchmark;

  const emptyResults =
    (!(hasCustomScore || isLoadingChannelCopies) &&
      isChannelConnected &&
      !benchmarkConfig?.shouldUpgrade) ||
    benchmarkConfig?.shouldUpgrade;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <CustomTypography className={styles.boxGroupTitle}>Custom score</CustomTypography>
        <Information
          className={styles.informationIcon}
          content={'See how this text compares with\ntext you published in the past.'}
          size={INFORMATION_SIZE.small}
          displayInvertedIcon
        />
      </div>
      <div>
        {loadingBenchmark ? (
          <Loader />
        ) : (
          <div
            className={cx(styles.container, {
              [styles.shouldUpgrade]: shouldShowUpgradeSection,
              [styles.isInfinity]: source === BENCHMARK_SOURCE.infinity,
            })}
          >
            {!(isChannelConnected || isLoadingChannelCopies) && (
              <Connect
                channel={channel}
                onConnectClick={handleConnectClick}
              />
            )}

            {isLoadingChannelCopies && (
              <>
                <CustomTypography
                  className={styles.subtitle}
                  variant={TYPOGRAPHY_VARIANT.regular14}
                >
                  Gathering and categorizing data from your account...
                </CustomTypography>
              </>
            )}

            {connectedAndHasBenchmark && (
              <ConnectedBenchmark
                onSettingsClick={() => openPopup({ channel, assetType })}
                generationTool={generationTool}
                benchmarkConfig={benchmarkConfig}
              />
            )}

            {emptyResults && (
              <EmptyResults
                comparisonSetName={benchmarkConfig?.comparisonSet?.name}
                onSettingsClick={() => openPopup({ channel, assetType })}
              />
            )}
          </div>
        )}
        {shouldShowUpgradeSection && <Upgrade onBenchmarkUpgradeClick={onBenchmarkUpgradeClick} />}
      </div>
    </div>
  );
};

Benchmark.propTypes = {
  classes: PropTypes.shape({}),
  channel: PropTypes.string.isRequired,
  onBenchmarkConnectClick: PropTypes.func,
  benchmarkConfig: PropTypes.shape({
    account: PropTypes.shape({
      name: PropTypes.string,
      channel: PropTypes.string,
    }),
    comparisonSet: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
      examples: PropTypes.array,
      userGoal: PropTypes.string,
    }),
    position: PropTypes.number,
    shouldUpgrade: PropTypes.bool,
    customScore: PropTypes.number,
  }),
  connectedAccounts: PropTypes.shape({}),
  loadingStatusMap: PropTypes.shape({}),
  loadingBenchmark: PropTypes.bool,
  source: PropTypes.string,
  shouldUpgrade: PropTypes.bool,
  featureLimitsData: PropTypes.shape({
    limit: PropTypes.number,
    currentCount: PropTypes.number,
    limitedAccountData: PropTypes.shape({
      accountId: PropTypes.string,
    }),
  }),
  openPopup: PropTypes.func.isRequired,
  onBenchmarkUpgradeClick: PropTypes.func.isRequired,
  openUpgradePopup: PropTypes.func.isRequired,
  assetType: PropTypes.string,
  generationTool: PropTypes.string,
};

Benchmark.defaultProps = {
  classes: null,
  connectedAccounts: null,
  onBenchmarkConnectClick: null,
  benchmarkConfig: null,
  loadingStatusMap: {},
  loadingBenchmark: false,
  source: null,
  shouldUpgrade: false,
  featureLimitsData: null,
  assetType: null,
  generationTool: null,
};
